<template>
  <div class="order-body">
    <br/>
    <h4>진행중 목록</h4>
    <div class="table-card">
      <div class="table-wrap position-relative">
        <div class="table-spinner-container" v-if="loadingStates.delivering">
            <div class="spinner" />
        </div>
        <a-table
            size="small"
            v-if="cashFlow.delivering.list.length > 0"
            :scroll="{ x: '2100px' }"
            :columns="columns.delivering"
            :data-source="cashFlow.delivering.list"
            :pagination="false"
        >
        </a-table>
        <div class="no-result-container" v-else>
            <div class="no-result-content">
                <i class="fa-solid fa-exclamation-triangle" v-if="!loadingStates.delivering"/>
                <span v-if="!loadingStates.delivering">조회 결과가 없습니다.</span>
            </div>
        </div>
      </div>
    </div>
    <br/>
    <h4>배송완료 및 입금 목록</h4>
    <div class="table-card">
        <div class="table-wrap position-relative">
            <div class="table-spinner-container" v-if="loadingStates.completed">
                <div class="spinner" />
            </div>
            <a-table
                size="small"
                :scroll="{ x: '2100px' }"
                v-if="cashFlow.completed.list.length > 0"
                :columns="columns.completed"
                :data-source="cashFlow.completed.list"
                :pagination="false"
            >
            </a-table>
            <div class="no-result-container" v-else>
                <div class="no-result-content">
                    <i class="fa-solid fa-exclamation-triangle" v-if="!loadingStates.completed"/>
                    <span v-if="!loadingStates.completed">조회 결과가 없습니다.</span>
                </div>
            </div>
        </div>
    </div>
    <pagination 
        :page-info="paging"
        :page-name="serverEnum.orderStatus.Completed"
        @change-page="paging.changePageListener"
    />

    <!-- 운송장 조회 모달 추가 -->
    <a-modal
      v-model:visible="isModalVisible"
      title="운송장 조회"
      :footer="null"
      @cancel="handleModalClose"
      class="delivery-modal"
    >
      <div v-if="deliveryInfo" class="delivery-modal-content">
        <div class="delivery-info">
          <div class="section-title">배송 정보</div>
          <div class="info-row">
            <span class="label">운송장 번호:</span>
            <span>{{ deliveryInfo.invoiceNo }}</span>
          </div>
          <div class="info-row">
            <span class="label">배송 상태:</span>
            <span>{{ deliveryInfo.deliveryStatus }}</span>
          </div>
        </div>
        <div class="tracking-history">
          <div class="section-title">배송 이력</div>
          <div v-for="(track, index) in deliveryInfo.trackingDetails" :key="index" class="track-item">
            <div class="time">{{ track.time }}</div>
            <div class="description">{{ track.description }}</div>
            <div class="location">{{ track.location }}</div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { serverEnum } from '../server-enum'

export default {
    name: 'CashFlow',
    props: {
        loadingStates: {
            type: Object,
            required: true
        },
        cashFlow: {
            type: Object,
            required: true
        },
        paging: {
            type: Object,
            required: true
        },
        searchDelivery: {
            type: Function,
            required: true
        }
    },
    data() {
        const baseColumns = [
            {key: 'index', title: '번호', dataIndex: 'index', align: 'center', fixed: 'left', width: 50},
            {key: 'hospitalName', title: '병원명', dataIndex: 'hospitalName', align: 'center', fixed: 'left', width: 140, ellipsis: true},
            {key: 'orderNo', title: '주문번호', dataIndex: 'orderNo', align: 'center', fixed: 'left', width: 140, ellipsis: true},
            {key: 'orderAt', title: '주문일', dataIndex: 'orderAt', align: 'center', fixed: 'left', width: 160},
            {key: 'deliveryType', title: '배송방식', dataIndex: 'deliveryType', align: 'center', width: 100},
            {
                key: 'deliveryCompany', 
                title: '배송회사', 
                dataIndex: 'deliveryCompany', 
                align: 'center',
                width: 100,
                customRender: ({record}) => {
                    if (record.deliveryNo?.length < 13 && record.deliveryNo !== '-') {
                        return <span>{'일양'}</span>
                    } else if (record.deliveryNo?.length >= 13 && record.deliveryNo !== '-') {
                        return <span>{'우체국'}</span>
                    } else {
                        return <span>{'-'}</span>
                    }
                }
            },
            {
                key: 'deliveryNo',
                title: '운송장',
                dataIndex: 'deliveryNo',
                align: 'center',
                width: 140,
                customRender: ({record}) => {
                    return (
                        <a-row style={{display: 'flex', justifyContent: 'center', justifyItems: 'center'}}>
                            <a-col>
                                <a-row>
                                    <span>{record.deliveryNo || '-'}</span>
                                </a-row>
                                <a-row>
                                    {record.deliveryType === '택배' && record.deliveryNo !== '-' &&
                                        <a-button
                                            style={{height: '10px', width: '100%'}}
                                            onClick={e => this.uiHandler.handleDeliveryInfoModal.open(record?.deliveryNo)}
                                        >
                                            조회
                                        </a-button>
                                    }
                                </a-row>
                            </a-col>
                        </a-row>
                    );
                }
            },
            {
                key: 'deliveryStatus',
                title: '배송상태',
                dataIndex: 'deliveryStatus',
                align: 'center',
                width: 120,
                customRender: ({record}) => {
                    return (record.deliveryStatus ?
                        <a-tag class={this.uiHandler.deliveryStatusClass(record)}>
                            <span style="font-size: 15px">{record.deliveryStatus}</span>
                        </a-tag> : <span>{'-'}</span>);
                }
            },
            {key: 'orderFlag', title: '주문분류', dataIndex: 'orderFlag', align: 'center', width: 100},
            {key: 'salesAmt', title: '총액', dataIndex: 'salesAmt', align: 'right', width: 120},
            {key: 'totalQty', title: '전체수량', dataIndex: 'totalQty', align: 'center', width: 100},
            {key: 'deptName', title: '지점', dataIndex: 'deptName', align: 'center', width: 120},
            {key: 'empName', title: '담당자', dataIndex: 'empName', align: 'center', width: 100},
            {key: 'orderType', title: '주문유형', dataIndex: 'orderType', align: 'center', width: 100},
            {
                key: 'remark',
                title: '비고',
                dataIndex: 'remark',
                align: 'center',
                width: 200,
                ellipsis: true
            }
        ];

        return {
            serverEnum,
            uiHandler: this.ui(),
            columns: {
                delivering: [...baseColumns],
                completed: [...baseColumns]
            },
            isModalVisible: false,
            deliveryInfo: null,
            currentDeliveryNo: null,
            trackingInfo: null,
        }
    },
    methods: {
        ui() {
            return {
                deliveryStatusClass(item) {
                    if (item.deliveryStatus === '배송 중') {
                        return 'blue';
                    } else if (item.deliveryStatus === '배송 완료' && item.orderFlag === '주문') {
                        return 'green';
                    } else {
                        return '';
                    }
                },
                handleDeliveryInfoModal: {
                    open: async (deliveryNo) => {
                        this.currentDeliveryNo = deliveryNo;
                        await this.getTrackingInfo(deliveryNo);
                    },
                    close: () => {
                        this.isModalVisible = false;
                        this.deliveryInfo = null;
                    }
                }
            }
        },
        async getTrackingInfo(deliveryNo) {
            try {
                await this.searchDelivery(
                    (response) => {
                        this.deliveryInfo = response.data;
                        this.isModalVisible = true;
                    },
                    async (error) => {
                        await this.$dialog.error({
                            title: '운송장 조회 실패',
                            content: error.message || '운송장 정보를 가져오는데 실패했습니다.'
                        });
                    },
                    deliveryNo
                );
            } catch (error) {
                console.error('Failed to get tracking info:', error);
            }
        },
        handleModalClose() {
            this.isModalVisible = false;
            this.deliveryInfo = null;
        }
    }
}
</script>

<style scoped lang="scss">
:deep(.ant-tag.blue) {
    color: #278AEC;
    background: #E5F1FC;
    border-color: #278AEC;
}

:deep(.ant-tag.green) {
    color: #2AB674;
    background: #E8F7F1;
    border-color: #2AB674;
}

.no-result-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  
  .no-result-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #8D939A;

    i {
      font-size: 24px;
    }

    span {
      font-size: 14px;
    }
  }
}

.delivery-info, .tracking-history {
  margin-bottom: 24px;
  
  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #2F3337;
  }
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  
  .label {
    width: 120px;
    font-weight: 500;
  }
}

.track-item {
  padding: 16px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  .time {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #2F3337;
    margin-bottom: 4px;
  }
  
  .location {
    font-size: 13px;
    color: #6E747C;
  }
}

.delivery-modal {
  :deep(.ant-modal-body) {
    padding: 24px;
  }

  :deep(.ant-modal-header) {
    .ant-modal-title {
      font-size: 18px;
      font-weight: 600;
    }
  }
}

.delivery-modal-content {
  padding: 0 20px;
}
</style> 